import { authConfig } from './config'
import { acquireApiToken } from './msal'

/**
 * The one way this app calls the JEDI API: bearer token on every request.
 * Step 4 of the wave wraps this with the OpenAPI-generated types; the
 * transport stays here.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await acquireApiToken()
  const response = await fetch(`${authConfig.apiBaseUrl}${path}`, {
    ...init,
    headers: { ...init?.headers, Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    // Carry the body: an error line that names its cause is debuggable; a
    // bare status trained us to guess (found live, wave-2 step 8).
    //
    // The cap was 300, which silently broke every STRUCTURED refusal longer
    // than that. A refusal that names what is in the way is routinely longer:
    // the completion refusal is 319 characters with two open steps on it, so it
    // arrived truncated, failed to parse, and the resolve tray never opened.
    // Found by pressing the button in a browser; 431 green tests had nothing to
    // say about it, because the API returns the right thing and it is the
    // TRANSPORT that loses it.
    const body = await response.text().catch(() => '')
    throw new Error(`API ${response.status} on ${path}${body ? `: ${body.slice(0, 4000)}` : ''}`)
  }
  /*
    AN EMPTY BODY IS A SUCCESSFUL ANSWER, and this used to throw on one.

    Three endpoints reply `TypedResults.Ok()` with nothing in it: disposing of a
    nudge, stamping a surface open, and authorizing a guardrail on a change set.
    `response.json()` on an empty body throws SyntaxError, so all three rejected
    a call the server had ALREADY COMMITTED. The visible symptom was Snooze and
    Dismiss appearing to do nothing: the event was written, the promise
    rejected, the mutation's onSuccess never ran, nothing invalidated, and the
    row sat there. A reload showed it gone, which is the tell.

    Found by Erik pressing Snooze on dev, 2026-09-20, and NOT by the preview
    harness, which fulfilled every route as `JSON.stringify(body)` and so could
    not produce the empty body that breaks this. The stub answers those three
    honestly now.

    Read as text and parse only if there is something to parse, rather than
    keying on 204: the shape being handled is "no body", and a 200 with an empty
    body is what these three actually send.
  */
  const body = await response.text()
  return (body ? JSON.parse(body) : undefined) as T
}

/**
 * Citation resolve, end to end (non-negotiable #2): opens the exact canonical
 * bytes behind a cited chunk as a download, with the bearer token the browser
 * cannot attach to a plain link.
 */
export async function apiDownload(path: string, fallbackFilename: string): Promise<void> {
  const token = await acquireApiToken()
  const response = await fetch(`${authConfig.apiBaseUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error(`API ${response.status} on ${path}`)
  const url = URL.createObjectURL(await response.blob())
  const anchor = document.createElement('a')
  anchor.href = url
  // The server names the real file in Content-Disposition (exposed by CORS
  // config); the caller's name is only the fallback.
  anchor.download = filenameFrom(response.headers.get('content-disposition')) ?? fallbackFilename
  anchor.click()
  URL.revokeObjectURL(url)
}

function filenameFrom(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utf8?.[1]) return decodeURIComponent(utf8[1])
  const plain = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return plain?.[1] ?? null
}
